/* global runtime */
// Dashboard-modell (forfall, oppfølging) og skattemeldingsgrunnlag (formue, kontakt, differanser).
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.

const importedSalesHistoryMonthlyRevenue = (state, year) => {
  const summary = state.accountingImportSalesHistorySummary?.years?.[String(year)];
  const monthly = Array.from({ length: 12 }, () => 0);
  safeArray(summary?.monthly).forEach((entry) => {
    const monthIndex = Number(entry?.month) - 1;
    if (monthIndex < 0 || monthIndex >= 12) return;
    monthly[monthIndex] += num(entry?.revenue);
  });
  return monthly;
};
const importedSalesHistoryRevenue = (state, year) =>
  num(state.accountingImportSalesHistorySummary?.years?.[String(year)]?.revenue);

// SAF-T-/rapportstatistikken er autoritativ for regnskapstall. Fakturahistorikken
// er bare fallback for importer som ikke inneholder noe rapportgrunnlag; ellers
// ville det samme salget blitt lagt oppa de importerte 3xxx-posteringene.
const importedSalesHistoryFallbackRevenue = (state, year) =>
  importedReportStatsForYear(state, year) ? 0 : importedSalesHistoryRevenue(state, year);

const addImportedSalesHistoryToAdvisorReport = (report, state, year, options = {}) => {
  const importedRevenue = Array.isArray(options.months)
    ? importedReportStatsForYear(state, year) ? 0 : importedSalesHistoryMonthlyRevenue(state, year).slice(options.months[0], options.months[1] + 1).reduce((sum, amount) => sum + amount, 0)
    : importedSalesHistoryFallbackRevenue(state, year);
  if (Math.abs(importedRevenue) <= 0.005) return report;
  return {
    ...report,
    inntekter: num(report.inntekter) + importedRevenue,
    totaleInntekter: num(report.totaleInntekter, num(report.inntekter)) + importedRevenue,
    resultat: num(report.resultat) + importedRevenue,
  };
};

const addImportedSalesHistoryToDashboardChart = (chart, state, year) => {
  if (importedReportStatsForYear(state, year)) return chart;
  const importedRevenue = importedSalesHistoryMonthlyRevenue(state, year);
  if (!importedRevenue.some((amount) => Math.abs(amount) > 0.005)) return chart;
  return chart.map((month, monthIndex) => ({
    ...month,
    inc: Math.round(num(month.inc) + importedRevenue[monthIndex]),
  }));
};

const dashboardDate = (value) => {
  const iso = nbToIso(value);
  const date = new Date(`${iso}T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const clampPercent = (value) => Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0));
const dashboardExpenseVatTreatment = (expense) => {
  if (expense.vatTreatment === 'vatOnly') return 'vatOnly';
  if (expense.vatTreatment === 'importBasis') return 'importBasis';
  if (expense.vatTreatment === 'reverseCharge' || expense.reverseChargeCode === '81' || expense.reverseChargeCode === '86') return 'reverseCharge';
  return num(expense.vatRate) === 0 ? 'zeroRated' : 'standard';
};
const dashboardExpenseDeductibleVatPercent = (expense) => {
  if (expense.vatDeductionMode === 'none') return 0;
  if (expense.vatDeductionMode === 'partial') return clampPercent(num(expense.deductibleVatPercent));
  return 100;
};
const dashboardExpenseLineItems = (expense) => {
  const lines = safeArray(expense.purchaseLines)
    .map((line) => ({
      amountNet: Math.max(0, num(line.amountNet)),
      vatAmount: line.vatAmount,
      vatRate: num(line.vatRate),
      vatTreatment: line.vatTreatment || (num(line.vatRate) === 0 ? 'zeroRated' : 'standard'),
      purchaseVatCode: line.purchaseVatCode || '',
      standardVatCode: line.standardVatCode || '',
      reverseChargeCode: line.reverseChargeCode || '', vatDeductionMode: line.vatDeductionMode || expense.vatDeductionMode, deductibleVatPercent: line.deductibleVatPercent ?? expense.deductibleVatPercent,
    }))
    .filter((line) => line.amountNet > 0);
  return lines.length ? lines : [{
    amountNet: Math.max(0, num(expense.amountNet)),
    vatAmount: expense.vatAmount,
    vatRate: num(expense.vatRate),
    vatTreatment: dashboardExpenseVatTreatment(expense),
    reverseChargeCode: expense.reverseChargeCode || '', vatDeductionMode: expense.vatDeductionMode, deductibleVatPercent: expense.deductibleVatPercent,
  }];
};
const dashboardExpenseVatBreakdown = (expense) => {
  return dashboardExpenseLineItems(expense).reduce((sum, line) => {
    const deductibleVatPercent = dashboardExpenseDeductibleVatPercent(line);
    const reverseCharge = line.vatTreatment === 'reverseCharge' || line.reverseChargeCode === '81' || line.reverseChargeCode === '86';
    const vatTreatment = reverseCharge ? 'reverseCharge' : ['vatOnly','importBasis'].includes(line.vatTreatment) ? line.vatTreatment : (num(line.vatRate) === 0 ? 'zeroRated' : 'standard');
    const hasExplicitVat = line.vatAmount !== undefined && line.vatAmount !== null && line.vatAmount !== '';
    const totalVat = voucherMoney(vatTreatment === 'vatOnly'
      ? line.amountNet
      : vatTreatment === 'standard' && hasExplicitVat
        ? Math.max(0, num(line.vatAmount))
        : vatTreatment === 'standard' || vatTreatment === 'reverseCharge' ? line.amountNet * (num(line.vatRate) / 100) : 0);
    const deductibleVat = totalVat * (deductibleVatPercent / 100);
    const nonDeductibleVat = totalVat - deductibleVat;
    const grossAmount = vatTreatment === 'vatOnly' ? totalVat : vatTreatment === 'reverseCharge' ? line.amountNet : line.amountNet + totalVat;
    const costAmount = vatTreatment === 'vatOnly' ? nonDeductibleVat : line.amountNet + nonDeductibleVat;
    sum.deductibleVat += deductibleVat;
    if (vatTreatment === 'reverseCharge') sum.reverseChargeOutputVat += totalVat;
    sum.grossAmount += grossAmount;
    sum.costAmount += costAmount;
    return sum;
  }, { deductibleVat: 0, reverseChargeOutputVat: 0, grossAmount: num(expense.roundingAmount), costAmount: num(expense.roundingAmount) });
};
const dashboardGrossExpense = (expense) => dashboardExpenseVatBreakdown(expense).grossAmount;
const dashboardCostExpense = (expense) => dashboardExpenseVatBreakdown(expense).costAmount;
const isFixedAssetExpense = (expense) => Boolean(expense.fixedAsset);
// Samme oppslag som activePaymentsForDocument, og deler na indeksen med den. Dette er
// hjem-modellens varmeste loekke: den kalles per apen faktura og per apent kjop.
const dashboardPaymentsForDocument = (state, documentType, documentId) =>
  activePaymentsForDocument(state, documentType, documentId);
const dashboardPaymentSummary = ({ totalAmount, payments, legacyPaid, paymentSplits, expectedDirection = 'outgoing', allocatedAmount = 0, allocatedFromAmount = 0 }) => {
  const safeTotalAmount = Math.max(0, num(totalAmount));
  const activePayments = payments.length
    ? payments
    : safeArray(paymentSplits)
      .filter((split) => split.settlementAccountCode !== '2400' && num(split.amount) > 0)
      .map((split) => ({ amount: split.amount }));
  const hasOpenSplit = safeArray(paymentSplits).some((split) => split.settlementAccountCode === '2400' && num(split.amount) > 0.005);
  if (legacyPaid && payments.length === 0 && !hasOpenSplit && !activePayments.length) return { paidAmount: safeTotalAmount, remainingAmount: 0, overpaidAmount: 0, status: 'paid' };
  const cashPaidAmount = activePayments.reduce((sum, payment) => {
    const amount = Math.max(0, num(payment.amount));
    return (payment.direction || expectedDirection) === expectedDirection ? sum + amount : sum - amount;
  }, 0);
  const paidAmount = Math.max(0, cashPaidAmount + Math.max(0, num(allocatedAmount)));
  const remainingAmount = Math.max(0, safeTotalAmount - paidAmount);
  const overpaidAmount = Math.max(0, cashPaidAmount - safeTotalAmount - Math.max(0, num(allocatedFromAmount)));
  return {
    paidAmount,
    remainingAmount,
    overpaidAmount,
    status: remainingAmount <= 0.005 && safeTotalAmount > 0 && (payments.length > 0 || !hasOpenSplit) ? 'paid' : paidAmount > 0.005 ? 'partial' : 'unpaid',
  };
};
const dashboardCreditedAmount = (state, invoice) => invoiceCreditedAmount(state, invoice);
// Use the settlement model for credits, refunds and transferred overpayments.
const dashboardInvoicePaymentSummary = (state, invoice) => invoicePaymentSummary(state, invoice);
const dashboardSupplierInvoicePaymentSummary = (state, expense) =>
  dashboardPaymentSummary({
    totalAmount: dashboardGrossExpense(expense),
    payments: dashboardPaymentsForDocument(state, 'supplier_invoice', expense.id),
    legacyPaid: Boolean(expense.supplierInvoicePaidAt),
    paymentSplits: expense.paymentSplits,
    expectedDirection: 'outgoing',
    allocatedAmount: safeArray(state.supplierSettlements).filter((entry) => entry.status !== 'reversed' && entry.kind === 'overpayment_offset' && entry.expenseId === expense.id).reduce((sum, entry) => sum + Math.max(0, num(entry.amount)), 0),
    allocatedFromAmount: safeArray(state.supplierSettlements).filter((entry) => entry.status !== 'reversed' && entry.kind === 'overpayment_offset' && entry.sourceExpenseId === expense.id).reduce((sum, entry) => sum + Math.max(0, num(entry.amount)), 0),
  });

const isDashboardDateInNextSevenDays = (value, today) => {
  const date = dashboardDate(value);
  if (!date) return false;
  const start = new Date(today);
  start.setHours(0, 0, 0, 0);
  const limit = new Date(start);
  limit.setDate(limit.getDate() + 7);
  return date >= start && date <= limit;
};

const dashboardIsFullCredit = (state, invoice) => {
  const gross = Math.max(0, invoiceGross(invoice));
  if (gross <= 0.005) return false;
  return dashboardCreditedAmount(state, invoice) >= gross - 0.005;
};

const dashboardHasAttachment = hasPrimaryAttachment;

const dashboardPlural = (count, singular, plural) => count === 1 ? singular : plural;
// Fristene kommer fra `vatDueDate`, ikke fra en egen tabell her. To kopier
// ville divergert paa sommerfristen for 3. termin (31. august, ikke 10.), og da
// ville Hjem og Innleveringer sagt hver sin dato om den samme terminen.
const dashboardVatTermLabel = (date) => `${date.getDate()}. ${VAT_MONTH_LABELS[date.getMonth()]}`;
const dashboardVatPeriod = (state, year) => globalThis.PlummeVatPeriodHistory?.forDate?.(state.settings, `${year}-01-01`) || state.settings?.vatPeriod || 'bimonthly';
const dashboardVatTermDeadlines = (year, state) => {
  const vatPeriod = dashboardVatPeriod(state, year);
  const country = String(state.settings?.countryCode || state.settings?.country || state.settings?.businessCountry || '').toUpperCase();
  if (country && country !== 'NO') return [];
  if (!state.settings?.vatRegistered) return [];
  if (vatPeriod !== 'bimonthly') {
    const annualDue = vatDueDate(year, 0, 'annual');
    return [{ key: 'annual', label: dashboardVatTermLabel(annualDue), dueDate: annualDue }];
  }
  return Array.from({ length: 6 }, (unused, index) => {
    const dueDate = vatDueDate(year, index, 'bimonthly');
    return { key: `term-${index + 1}`, label: dashboardVatTermLabel(dueDate), dueDate };
  });
};

const dashboardIsIsoDate = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);

const dashboardDateString = (date) => date.toISOString().slice(0, 10);

const dashboardVatTermRange = (year, termKey) => {
  const term = Number(String(termKey).replace('term-', ''));
  if (!Number.isFinite(term) || term < 1 || term > 6) return null;
  const startMonth = (term - 1) * 2;
  return {
    dateFrom: dashboardDateString(new Date(Date.UTC(year, startMonth, 1))),
    dateTo: dashboardDateString(new Date(Date.UTC(year, startMonth + 2, 0))),
  };
};

const dashboardRelevantVatTermDeadlines = (year, state) => {
  const deadlines = dashboardVatTermDeadlines(year, state);
  const vatPeriod = dashboardVatPeriod(state, year);
  const registeredFrom = state.settings?.vatRegisteredFrom;
  const deregisteredFrom = state.settings?.vatDeregisteredFrom;
  const hasRegistrationBoundary = dashboardIsIsoDate(registeredFrom) || dashboardIsIsoDate(deregisteredFrom);
  if (vatPeriod !== 'bimonthly' || !hasRegistrationBoundary) return deadlines;

  return deadlines.filter((deadline) => {
    const range = dashboardVatTermRange(year, deadline.key);
    if (!range) return true;
    const startsBeforeDeregistration = !dashboardIsIsoDate(deregisteredFrom) || range.dateFrom < deregisteredFrom;
    const endsAfterRegistration = !dashboardIsIsoDate(registeredFrom) || range.dateTo >= registeredFrom;
    return startsBeforeDeregistration && endsAfterRegistration;
  });
};

// Levert og gjort opp er to spoersmaal. Det foerste har et flagg; det andre har
// det bare naar noen har huket av manuelt. Et importert regnskap kommer med
// betalingsspor i stedet for avhuking, og en termin som ble foert i det gamle
// systemet har uansett ingenting aa gjoere opp i Plumme. Derfor samme regel som
// `buildVatFilingModel` — ellers sier Hjem «ikke betalt» om terminer
// Innleveringer viser som «levert og betalt».
const dashboardVatTermSettled = (state, year, deadline) => {
  const deadlineStatus = state.deadlinesByYear?.[String(year)] || {};
  const vatPeriod = dashboardVatPeriod(state, year);
  const paidFlag = vatPeriod === 'bimonthly'
    ? Boolean(deadlineStatus.mvaPaidTerms?.[deadline.key])
    : Boolean(deadlineStatus.mvaPaid);
  if (paidFlag) return true;
  const filingTerm = safeArray(getVatFilingModel({ year })?.terms).find((term) =>
    term.key === deadline.key ||
    term.legacyKey === deadline.key ||
    (deadline.key === 'annual' && term.key === 'mvaSubmitted'));
  return filingTerm?.status === 'levert-betalt';
};

const dashboardReportingPartsForYear = (state, year, today) => {
  const deadlineStatus = state.deadlinesByYear?.[String(year)] || {};
  const vatPeriod = dashboardVatPeriod(state, year);
  return dashboardRelevantVatTermDeadlines(year, state)
    .filter((deadline) => deadline.dueDate < today)
    .reduce((parts, deadline) => {
      const submitted = vatPeriod === 'bimonthly'
        ? Boolean(deadlineStatus.mvaTerms?.[deadline.key])
        : Boolean(deadlineStatus.mvaSubmitted);
      const paid = dashboardVatTermSettled(state, year, deadline);
      const label = deadline.label.includes(String(deadline.dueDate.getFullYear()))
        ? deadline.label
        : `${deadline.label} ${deadline.dueDate.getFullYear()}`;
      const key = { deadlineId: deadline.key, deadlineYear: year, name: `MVA-melding for ${year}`, dueLabel: label };
      if (!submitted) {
        parts.submissionItems.push({ ...key, part: 'submission', text: `MVA-melding (${label}) for ${year} er forfalt og ikke levert.` });
      }
      if (!paid) {
        parts.paymentItems.push({ ...key, part: 'payment', text: `MVA-melding (${label}) for ${year} er forfalt og ikke markert som betalt.` });
      }
      return parts;
    }, { submissionItems: [], paymentItems: [] });
};

// Kontoene som maa avstemmes. Speiler `AP_PERIOD_MUST_RECONCILE` i
// autopilot-periods.jsx og `settingsBankMustReconcile` i misc.jsx.
const DASHBOARD_MUST_RECONCILE_KINDS = ['operating', 'tax_withholding'];

// «Avstemt til og med» settes av regnskapsimporten og av manuell oppstart, og er
// datoen banken sist ble gjort opp i det gamle systemet. Maanedene foer den er
// en annens arbeid — Plumme har ingen utskrift for dem og skal ikke etterlyse
// en. Er EN konto uten dato, er ingenting avstemt: maaneden bestaar av alle
// kontoene. Speiler `apBankCoverageFrom` i autopilot-periods.jsx.
const dashboardBankSettledThrough = (state) => {
  const accounts = safeArray(state.settings?.bankAccounts)
    .filter((account) => account?.active !== false)
    .filter((account) => DASHBOARD_MUST_RECONCILE_KINDS.includes(String(account?.kind || 'operating')) || account?.reconcile === true);
  if (!accounts.length) return '';
  const dates = accounts.map((account) => String(account?.reconciledThrough || account?.startBalanceDate || ''));
  if (dates.some((date) => !dashboardIsIsoDate(date))) return '';
  return dates.sort()[0];
};

// Siste dag i maaneden som ISO, i UTC slik at en lokal tidssone ikke skyver
// datoen en dag tilbake. En maaned er daekket foerst naar hele den er det.
const dashboardMonthEndIso = (year, monthIndex) =>
  new Date(Date.UTC(year, monthIndex + 1, 0)).toISOString().slice(0, 10);

const dashboardMissingBankMonths = (state, year) => {
  const configured = safeArray(state.settings?.bankAccounts);
  const accounts = configured.filter(account => account.active !== false && (DASHBOARD_MUST_RECONCILE_KINDS.includes(String(account.kind || 'operating')) || account.reconcile === true));
  const scopes = configured.length ? accounts : [{}];
  const now = new Date();
  const expectedMonthCount = year < now.getFullYear() ? 12 : year > now.getFullYear() ? 0 : now.getMonth() + 1;
  const missing = new Set();
  scopes.forEach(account => {
    const enabledFrom = account.reconciliationFrom || state.settings?.bankReconciliationEnabledFrom || `${year}-01-01`;
    const settledThrough = account.reconciledThrough || account.startBalanceDate || '';
    const coveredMonths = new Set();
    safeArray(state.bankStatements).filter(statement => statement.source === 'business' && (!statement.bankAccountId || !account.id || statement.bankAccountId === account.id)).forEach(statement => {
      safeArray(statement.lines).forEach(line => {
        const day = dashboardDate(line.date);
        if (day && day.getFullYear() === year) coveredMonths.add(day.getMonth());
      });
    });
    for (let monthIndex = 0; monthIndex < expectedMonthCount; monthIndex++) {
      const end = dashboardMonthEndIso(year, monthIndex);
      if (dashboardIsIsoDate(enabledFrom) && end < enabledFrom) continue;
      if (dashboardIsIsoDate(settledThrough) && end <= settledThrough) continue;
      if (!coveredMonths.has(monthIndex)) missing.add(monthIndex);
    }
  });
  return [...missing].sort((a,b)=>a-b).map(monthIndex => ({ monthIndex, label: window.PlummeI18n.date(new Date(year, monthIndex, 1), {month:'long'}) }));
};

const buildDashboardFollowupGroups = ({ state, year, today }) => {
  const allInvoices = safeArray(state.invoices);
  const yearInvoices = allInvoices.filter((invoice) => String(invoice.date || invoice.createdAt || '').slice(0, 4) === String(year));
  const activeInvoices = allInvoices.filter((invoice) => invoice.status !== 'draft' && !invoice.archivedAt);
  // `conversationSummary` er sannheten etter at dialogene flyttet ut av app-state.
  // Fallback til traadene selv saa lenge en kunde ikke er migrert enda.
  const conversationFlag = (customer, key, status) => (customer.conversationSummary
    ? Boolean(customer.conversationSummary[key])
    : safeArray(customer.conversationThreads).some((thread) => thread.status === status));
  const customerWaitingReplyCount = safeArray(state.customers).filter((customer) =>
    customer.status !== 'archived' && conversationFlag(customer, 'waiting', 'customer_waiting'),
  ).length;
  const customerReadyForOfferCount = safeArray(state.customers).filter((customer) =>
    customer.status !== 'archived' && conversationFlag(customer, 'readyForOffer', 'ready_for_offer'),
  ).length;
  const sentOffersAwaitingAcceptanceCount = activeInvoices.filter((invoice) =>
    invoice.kind === 'offer' && invoice.sentAt && !invoice.offerAcceptedAt,
  ).length;
  const sentOffersExpiringSoonCount = activeInvoices.filter((invoice) =>
    invoice.kind === 'offer' &&
    invoice.sentAt &&
    !invoice.offerAcceptedAt &&
    isDashboardDateInNextSevenDays(invoice.validUntil, today),
  ).length;
  const sentOffersWithUpcomingServiceDateCount = activeInvoices.filter((invoice) =>
    invoice.kind === 'offer' &&
    invoice.sentAt &&
    !invoice.offerAcceptedAt &&
    (isDashboardDateInNextSevenDays(invoice.serviceDate, today) || isDashboardDateInNextSevenDays(invoice.serviceEndDate, today)),
  ).length;
  const acceptedOffersMissingOrderConfirmationCount = activeInvoices.filter((invoice) =>
    invoice.kind === 'offer' && invoice.offerAcceptedAt && !invoice.orderConfirmationSentAt,
  ).length;
  const upcomingCustomerOrdersCount = activeInvoices.filter((invoice) =>
    (invoice.kind === 'order_confirmation' || (invoice.kind === 'offer' && invoice.offerAcceptedAt)) &&
    (isDashboardDateInNextSevenDays(invoice.serviceDate, today) || isDashboardDateInNextSevenDays(invoice.serviceEndDate, today)),
  ).length;
  const unsentInvoicesCount = yearInvoices.filter((invoice) =>
    invoice.kind !== 'offer' &&
    invoice.kind !== 'order_confirmation' &&
    invoice.kind !== 'credit' &&
    invoice.status !== 'draft' &&
    !dashboardIsFullCredit(state, invoice) &&
    !invoice.sentAt,
  ).length;
  const overdueUnpaidInvoicesCount = yearInvoices.filter((invoice) =>
    invoice.status !== 'draft' &&
    invoice.kind !== 'credit' &&
    invoice.kind !== 'offer' &&
    invoice.kind !== 'order_confirmation' &&
    invoice.sentAt &&
    !dashboardIsFullCredit(state, invoice) &&
    dashboardInvoicePaymentSummary(state, invoice).status !== 'paid' &&
    dashboardDate(invoice.dueDate) &&
    dashboardDate(invoice.dueDate) < today,
  ).length;
  const invoiceLinesWithoutCorrectVatTreatmentCount = yearInvoices
    .filter((invoice) => invoice.status !== 'draft' && invoice.kind !== 'offer' && invoice.kind !== 'order_confirmation')
    .flatMap((invoice) => safeArray(invoice.lines))
    .filter((line) => num(line.vatRate) === 0 && line.vatTreatment === 'standard').length;
  const otherSalesWithoutAttachmentsCount = safeArray(state.otherSales)
    .filter((sale) => sale.status !== 'annulled' && !dashboardHasAttachment(sale)).length;
  const otherSalesWithoutCorrectVatTreatmentCount = safeArray(state.otherSales)
    .filter((sale) => sale.status !== 'annulled' && num(sale.vatRate) === 0 && sale.vatTreatment === 'standard').length;
  const unsentOffersCount = yearInvoices
    .filter((invoice) => invoice.status !== 'draft' && invoice.kind === 'offer' && !invoice.sentAt).length;
  const purchasesWithoutAttachmentsCount = safeArray(state.expenses)
    .filter((expense) => entryYear(expense) === year && isActiveExpense(expense) && !attachmentMarkedLost(expense) && !isMileageExpense(expense) && !isFreePostingExpense(expense) && !dashboardHasAttachment(expense)).length;
  const unpaidSupplierInvoicesCount = dashboardOpenSupplierInvoices(state).length;
  // Papirkurven er ikke innboksen. Et bilag brukeren har slettet er hverken
  // bokfoert eller ubehandlet, og skal ikke telles som en oppgave — det er
  // samme skille som `buildReceiptInboxModel` gjoer for selve innboksen.
  const unbookedReceiptInboxCount = safeArray(state.receiptInbox)
    .filter((entry) => entry.status !== 'booked' && !receiptInboxEntryIsTrashed(entry)).length;
  const previousYear = year - 1;
  const previousYearDeadlineStatus = state.deadlinesByYear?.[String(previousYear)];
  const hasPreviousYearAccounting =
    previousYear >= num(state.settings?.startYear, previousYear) &&
    (
      Boolean(previousYearDeadlineStatus) ||
      allInvoices.some((invoice) => String(invoice.date || invoice.createdAt || '').slice(0, 4) === String(previousYear)) ||
      safeArray(state.expenses).some((expense) => String(expense.date || expense.createdAt || '').slice(0, 4) === String(previousYear)) ||
      safeArray(state.otherSales).some((sale) => String(sale.date || sale.createdAt || '').slice(0, 4) === String(previousYear))
    );
  const currentReportingParts = dashboardReportingPartsForYear(state, year, today);
  const previousReportingParts = hasPreviousYearAccounting
    ? dashboardReportingPartsForYear(state, previousYear, today)
    : { submissionItems: [], paymentItems: [] };
  const reportingItems = [
    ...currentReportingParts.submissionItems,
    ...previousReportingParts.submissionItems,
    ...currentReportingParts.paymentItems,
    ...previousReportingParts.paymentItems,
  ];
  const missingBankMonths = dashboardMissingBankMonths(state, year);
  const bankItems = missingBankMonths.length
    ? [{
      text: `Driftskonto mangler bankutskrift for ${missingBankMonths.map((month) => month.label).join(', ')}`,
      i18nKey: 'home.followup.missingBankStatements',
      monthIndexes: missingBankMonths.map((month) => month.monthIndex),
    }]
    : [];
  // MVA-registrert uten registreringsdato er ikke en manglende opplysning — det er
  // en feilkilde som virker begge veier. Uten datoen behandles foretaket som
  // MVA-pliktig saa langt tilbake dataene rekker: kjop fra for registreringen faar
  // fradrag foretaket ikke hadde, og MVA-meldinger dukker opp for terminer det
  // ikke var registrert i. Derfor kritisk, ikke en paaminnelse.
  const vatRegisteredWithoutDate = state.settings?.vatRegistered === true
    && !dashboardIsIsoDate(state.settings?.vatRegisteredFrom);
  // En bekreftelse ved opprettelsen sier «registrert da regnskapet startet» — og
  // ingenting om aarene foer. Flyttes startaaret bakover, eller hentes et tidligere
  // aar inn med en regnskapsimport, daekker den ikke lenger perioden. Da er
  // spoersmaalet aapent igjen, ikke besvart. Speiler `vatRegistrationReview` i
  // functions/accounting/vatRegistrationTiming.ts.
  const dashboardEarliestAccountingYear = (() => {
    const years = [Number(state.settings?.startYear)]
      .concat(Object.keys(state.openingBalanceByYear || {}).map(Number))
      .filter((year) => Number.isInteger(year) && year >= 1900 && year <= 2200);
    return years.length ? Math.min(...years) : null;
  })();
  const vatRegistrationConfirmationOutdated = state.settings?.vatRegistered === true
    && state.settings?.vatRegisteredFromSource === 'confirmed-at-start'
    && dashboardIsIsoDate(state.settings?.vatRegisteredFrom)
    && dashboardEarliestAccountingYear !== null
    && dashboardEarliestAccountingYear < Number(String(state.settings.vatRegisteredFrom).slice(0, 4));

  const followupTarget = (text, target) => text ? { text, ...target } : null;

  return [...window.PlummeFixedAssetPostingFollowup(state),
    {
      id: 'customer-followup',
      title: 'Kundeoppfølging',
      route: 'kunder',
      actionLabel: 'Gå til kundeliste',
      items: [
        customerWaitingReplyCount > 0 ? `${customerWaitingReplyCount} ${dashboardPlural(customerWaitingReplyCount, 'kunde venter', 'kunder venter')} på svar.` : null,
        customerReadyForOfferCount > 0 ? `${customerReadyForOfferCount} kundedialog${customerReadyForOfferCount === 1 ? '' : 'er'} ${customerReadyForOfferCount === 1 ? 'er klar' : 'er klare'} for tilbud.` : null,
        sentOffersAwaitingAcceptanceCount > 0 ? `${sentOffersAwaitingAcceptanceCount} tilbud er sendt, men mangler aksept.` : null,
        sentOffersExpiringSoonCount > 0 ? `${sentOffersExpiringSoonCount} tilbud utløper innen 7 dager.` : null,
        sentOffersWithUpcomingServiceDateCount > 0 ? `${sentOffersWithUpcomingServiceDateCount} tilbud har planlagt dato innen 7 dager.` : null,
        acceptedOffersMissingOrderConfirmationCount > 0 ? `${acceptedOffersMissingOrderConfirmationCount} tilbud er akseptert, men mangler ordrebekreftelse.` : null,
        upcomingCustomerOrdersCount > 0 ? `${upcomingCustomerOrdersCount} kundeordre nærmer seg de neste 7 dagene.` : null,
      ].filter(Boolean),
    },
    {
      id: 'sales',
      title: 'Salg',
      route: 'fakturaliste',
      actionLabel: 'Gå til fakturaliste',
      targetTab: 'faktura',
      items: [
        followupTarget(
          unsentInvoicesCount > 0 ? `Du har ${unsentInvoicesCount} faktura${unsentInvoicesCount === 1 ? '' : 'er'} uten sendt-status.` : null,
          { route: 'fakturaliste', targetTab: 'faktura', targetInvoiceFilter: { checks: { opprettet: true } } },
        ),
        followupTarget(
          overdueUnpaidInvoicesCount > 0 ? `Du har ${overdueUnpaidInvoicesCount} uoppgjorte forfalte faktura${overdueUnpaidInvoicesCount === 1 ? '' : 'er'}.` : null,
          { route: 'fakturaliste', targetTab: 'faktura', targetInvoiceFilter: { checks: { forfalt: true } } },
        ),
        invoiceLinesWithoutCorrectVatTreatmentCount > 0 ? `Du har ${invoiceLinesWithoutCorrectVatTreatmentCount} fakturalinje${invoiceLinesWithoutCorrectVatTreatmentCount === 1 ? '' : 'r'} med 0 % MVA uten valgt riktig behandling.` : null,
      ].filter(Boolean),
    },
    {
      id: 'other-sales',
      title: 'Annet salg',
      route: 'allebilag',
      targetVoucherFilter: { checks: { annetsalg: true, ...(otherSalesWithoutAttachmentsCount > 0 ? { manglervedlegg: true } : {}) } },
      actionLabel: 'Gå til bilagsliste',
      items: [
        followupTarget(
          otherSalesWithoutAttachmentsCount > 0 ? `Du har ${otherSalesWithoutAttachmentsCount} annet salg uten bilag.` : null,
          { route: 'allebilag', targetVoucherFilter: { checks: { annetsalg: true, manglervedlegg: true } } },
        ),
        followupTarget(
          otherSalesWithoutCorrectVatTreatmentCount > 0 ? `Du har ${otherSalesWithoutCorrectVatTreatmentCount} annet salg med 0 % MVA uten valgt riktig behandling.` : null,
          { route: 'allebilag', targetVoucherFilter: { checks: { annetsalg: true } } },
        ),
      ].filter(Boolean),
    },
    {
      id: 'offers',
      title: 'Tilbud',
      route: 'fakturaliste',
      targetTab: 'tilbud',
      targetInvoiceFilter: { checks: { opprettet: true } },
      actionLabel: 'Gå til tilbudsliste',
      items: [followupTarget(
        unsentOffersCount > 0 ? `Du har ${unsentOffersCount} tilbud uten sendt-status.` : null,
        { route: 'fakturaliste', targetTab: 'tilbud', targetInvoiceFilter: { checks: { opprettet: true } } },
      )].filter(Boolean),
    },
    {
      id: 'purchases',
      title: 'Kjøp',
      route: 'allebilag',
      targetVoucherFilter: { checks: { kjop: true, manglervedlegg: true } },
      actionLabel: 'Gå til bilagsliste',
      items: [followupTarget(
        purchasesWithoutAttachmentsCount > 0 ? `Du har ${purchasesWithoutAttachmentsCount} kjøp uten bilag.` : null,
        { route: 'allebilag', targetVoucherFilter: { checks: { kjop: true, manglervedlegg: true } } },
      )].filter(Boolean),
    },
    {
      id: 'suppliers',
      title: 'Leverandører',
      route: 'allebilag',
      targetVoucherFilter: { checks: { leverandorfaktura: true, ubetalt: true } },
      actionLabel: 'Gå til bilagsliste',
      items: [
        followupTarget(
          unpaidSupplierInvoicesCount > 0 ? `Du har ${unpaidSupplierInvoicesCount} ubetalte leverandørfaktura${unpaidSupplierInvoicesCount === 1 ? '' : 'er'}.` : null,
          { route: 'allebilag', targetVoucherFilter: { checks: { leverandorfaktura: true, ubetalt: true } } },
        ),
        safeArray(state.suppliers).length === 0 && unpaidSupplierInvoicesCount > 0 ? 'Registrer leverandører for bedre oppfølging.' : null,
      ].filter(Boolean),
    },
    {
      id: 'inbox',
      title: 'Innboks',
      route: 'innboks',
      actionLabel: 'Gå til innboks',
      items: [unbookedReceiptInboxCount > 0 ? `Du har ${unbookedReceiptInboxCount} bilag i innboksen som ikke er bokført ennå.` : null].filter(Boolean),
    },
    {
      id: 'vat-registration',
      title: 'MVA',
      route: 'innstillinger',
      actionLabel: 'Gå til innstillinger',
      items: [vatRegisteredWithoutDate ? {
        text: 'Foretaket er MVA-registrert, men mangler dato for når registreringen gjelder fra. Uten datoen føres MVA på kjøp og salg også for tiden før foretaket var registrert.',
        tone: 'critical',
        route: 'innstillinger',
      } : null, vatRegistrationConfirmationOutdated ? {
        text: `MVA-datoen ble bekreftet som «registrert fra regnskapets start», men regnskapet dekker nå ${dashboardEarliestAccountingYear} og tidligere. Bekreft datoen på nytt for perioden før ${String(state.settings.vatRegisteredFrom).slice(0, 4)}.`,
        tone: 'critical',
        route: 'innstillinger',
      } : null].filter(Boolean),
    },
    {
      id: 'reporting',
      title: 'Innleveringer',
      route: 'innsendinger',
      actionLabel: 'Gå til innleveringer',
      items: reportingItems,
    },
    {
      id: 'bank',
      title: 'Bankavstemming',
      route: 'bank',
      actionLabel: 'Gå til bankavstemming',
      items: bankItems,
    },
  ].filter((group) => group.items.length > 0);
};

const dashboardOpenInvoices = (state) =>
  safeArray(state.invoices).filter((invoice) => {
    if (!isPostedInvoice(invoice, state) || invoice.kind === 'credit') return false;
    const summary = dashboardInvoicePaymentSummary(state, invoice);
    return summary.status !== 'paid' && summary.remainingAmount > 0.005;
  });

const dashboardOpenSupplierInvoices = (state) =>
  safeArray(state.expenses).filter((expense) => {
    if (!isActiveExpense(expense) || !expense.isSupplierInvoice) return false;
    const summary = dashboardSupplierInvoicePaymentSummary(state, expense);
    return summary.status !== 'paid' && summary.remainingAmount > 0.005;
  });

const yearClosingVatStatus = (state, year) => {
  if (state.settings?.vatRegistered === false) return { label: t('vat.notRegistered'), notApplicable: true, allSubmitted: true, openTerms: 0 };
  const filing = getVatFilingModel({ year });
  if (state.settings?.vatRegistered !== true || !safeArray(filing?.terms).length) return { label: t('clo.vatUnknown', 'MVA-status ukjent'), allSubmitted: false, openTerms: 1 };
  const relevantKeys = new Set(dashboardRelevantVatTermDeadlines(year, state).map((term) => term.key));
  const terms = dashboardVatPeriod(state, year) === 'bimonthly' ? [...relevantKeys].map((key) => safeArray(filing.terms).find((term) => term.key === key) || { key, n: Number(key.replace('term-', '')), status: 'missing' }) : safeArray(filing.terms);
  const openTerms = terms.filter((term) => !['levert', 'levert-betalt'].includes(term.status));
  if (!openTerms.length) return { label: terms.length ? t('clo.vatAllFiled', 'Alle terminer levert') : t('clo.vatNoRelevant', 'Ingen relevante MVA-terminer i året'), notApplicable: !terms.length, allSubmitted: true, openTerms: 0 };
  const label = t('clo.vatMissingTerms', 'Ikke levert for {{year}}: {{terms}}', { year, terms: openTerms.map((term) => t('clo.vatTermNumber', '{{n}}. termin', { n: term.n })).join(', ') });
  return { label, allSubmitted: false, openTerms: openTerms.length };
};

const trialAccountValue = (reports, code) => safeArray(reports?.trial)
  .flatMap((group) => safeArray(group.accounts))
  .find((account) => String(account.nr || account.code || '') === String(code))?.ut || 0;

const sumTrialAccounts = (reports, predicate) =>
  safeArray(reports?.trial)
    .flatMap((group) => safeArray(group.accounts))
    .reduce((sum, account) => {
      const code = String(account.nr || account.code || '');
      return predicate(code, account) ? sum + num(account.ut) : sum;
    }, 0);

const sumTrialAccountCodes = (reports, codes) =>
  safeArray(codes).reduce((sum, code) => sum + num(trialAccountValue(reports, code)), 0);

const trialAccountRows = (reports) =>
  safeArray(reports?.trial)
    .flatMap((group) => safeArray(group.accounts))
    .map((account) => {
      const code = String(account.nr || account.code || '').trim();
      const name = String(account.name || account.label || account.title || '').trim();
      return {
        code,
        label: [code, name || `Konto ${code}`].filter(Boolean).join(' - '),
        amount: Math.round(num(account.ut) * 100) / 100,
      };
    })
    .filter((row) => row.code && Math.abs(row.amount) > 0.005)
    .sort((a, b) => Number(a.code) - Number(b.code));

const isTrialAccountInRange = (row, from, to) => {
  const code = String(row.code || '').trim();
  const importedSubaccount = code.match(/^(\d{4})[:._-]/);
  const nr = importedSubaccount ? Number(importedSubaccount[1]) : Number(code);
  return nr >= from && nr <= to;
};

const settingsCompanyType = (settings = {}) => String(
  settings.companyTypeCode ||
  settings.companyType ||
  settings.businessType ||
  settings.organizationType ||
  settings.companyTypeLabel ||
  '',
).toUpperCase();

const isEnkCompanyType = (companyType) =>
  companyType === 'ENK' ||
  companyType === 'SOLE_PROPRIETORSHIP' ||
  companyType.includes('ENKELTPERSON');

const resolveAccountingScope = (settings = {}, fiscalYear = new Date().getFullYear()) => {
  if (!globalThis.PlummeAccountingScope?.resolveAccountingScope) {
    throw new Error('Accounting scope service is not loaded.');
  }
  return globalThis.PlummeAccountingScope.resolveAccountingScope(settings, fiscalYear);
};

const taxReturnContactModel = (state, company) => {
  const profile = runtime.userProfile || {};
  const user = runtime.user || {};
  const settings = state.settings || {};
  const name = settings.contactName ||
    settings.contactPerson ||
    profile.displayName ||
    profile.name ||
    user.displayName ||
    company.name ||
    'Kontaktperson';
  const email = settings.contactEmail ||
    profile.email ||
    user.email ||
    company.email ||
    '';
  const phone = settings.contactPhone ||
    settings.phone ||
    settings.telephone ||
    profile.phoneNumber ||
    user.phoneNumber ||
    '';
  const mobile = settings.contactMobile ||
    settings.mobile ||
    settings.mobilePhone ||
    phone ||
    '';
  return {
    navn: name,
    tlf: phone || '-',
    epost: email || '-',
    mobil: mobile || '-',
  };
};

const normalizeTaxBasisPermanentDifferences = (rows) =>
  safeArray(rows)
    .map((entry) => stripUndefinedDeep({
      id: String(entry?.id || `perm-${Date.now()}-${Math.random().toString(16).slice(2)}`),
      code: String(entry?.code || '').trim(),
      label: String(entry?.label || '').trim(),
      kind: entry?.kind === 'fradrag' ? 'fradrag' : 'tillegg',
      amount: Math.round(num(entry?.amount) * 100) / 100,
      desc: String(entry?.desc || '').trim(),
    }))
    .filter((entry) => entry.code && entry.label);

const normalizeTaxBasisTemporaryDifferences = (rows) =>
  safeArray(rows)
    .map((entry) => stripUndefinedDeep({
      id: String(entry?.id || `midl-${Date.now()}-${Math.random().toString(16).slice(2)}`),
      code: String(entry?.code || '').trim(),
      label: String(entry?.label || '').trim(),
      rmFjor: Math.round(num(entry?.rmFjor) * 100) / 100,
      smFjor: Math.round(num(entry?.smFjor) * 100) / 100,
      rmAar: Math.round(num(entry?.rmAar) * 100) / 100,
      smAar: Math.round(num(entry?.smAar) * 100) / 100,
      desc: String(entry?.desc || '').trim(),
    }))
    .filter((entry) => entry.code && entry.label);
const normalizeTaxBasisLossCarryforward = (value = {}) => ({
  fremfortTidl: Math.round(num(value.fremfortTidl) * 100) / 100,
  underhand: value.underhand == null ? null : Math.round(num(value.underhand) * 100) / 100,
  restUnderhand: value.restUnderhand == null ? null : Math.round(num(value.restUnderhand) * 100) / 100,
  aaretsAnv: Math.round(num(value.aaretsAnv) * 100) / 100,
  openingConfirmed: value.openingConfirmed === true, openingSource: ['prior_year', 'manual'].includes(value.openingSource) ? value.openingSource : undefined,
});
const normalizeTaxBasisExtraSpecValues = (value = {}) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value)
      .filter(([title]) => typeof title === 'string' && title.trim())
      .map(([title, spec]) => [
        title,
        stripUndefinedDeep({
          fields: spec && typeof spec === 'object' && !Array.isArray(spec.fields) && spec.fields
            ? Object.fromEntries(Object.entries(spec.fields).map(([key, entry]) => [String(key), entry == null ? null : String(entry)]))
            : {},
          tables: spec && typeof spec === 'object' && spec.tables
            ? Object.fromEntries(Object.entries(spec.tables).map(([key, rows]) => [String(key), safeArray(rows).map((row) => safeArray(row).map((cell) => String(cell || '')))]))
            : {},
          schemes: spec && typeof spec === 'object' && spec.schemes
            ? Object.fromEntries(Object.entries(spec.schemes).map(([key, rows]) => [String(key), safeArray(rows).map((entry) => stripUndefinedDeep({
              id: String(entry?.id || `scheme-${Date.now()}-${Math.random().toString(16).slice(2)}`),
              value: String(entry?.value || ''),
            })).filter((entry) => entry.value)]))
            : {},
        }),
      ]),
  );
};
const normalizeTaxBasisModel = (taxBasis = {}) => {
  const permanentDifferences = normalizeTaxBasisPermanentDifferences(taxBasis.permanentDifferences);
  const temporaryDifferences = normalizeTaxBasisTemporaryDifferences(taxBasis.temporaryDifferences);
  const lossCarryforward = normalizeTaxBasisLossCarryforward(taxBasis.lossCarryforward);
  return {
    permanentDifferences,
    temporaryDifferences,
    lossCarryforward,
    deferredTaxAssetOverride: taxBasis.deferredTaxAssetOverride == null ? null : Math.round(num(taxBasis.deferredTaxAssetOverride) * 100) / 100,
    extraSpecs: Array.from(new Set(safeArray(taxBasis.extraSpecs).map((entry) => String(entry || '').trim()).filter(Boolean))).slice(0, 40),
    extraSpecValues: normalizeTaxBasisExtraSpecValues(taxBasis.extraSpecValues),
  };
};
const normalizeTaxSubmissionModel = (submission = {}) => stripUndefinedDeep({
  period: submission.period == null ? undefined : String(submission.period || '').trim().slice(0, 80),
  accountingRuleType: submission.accountingRuleType == null ? undefined : String(submission.accountingRuleType || '').trim().slice(0, 160),
  auditor: ['Ja', 'Nei'].includes(String(submission.auditor || '')) ? String(submission.auditor) : undefined,
  submitter: submission.submitter == null ? undefined : String(submission.submitter || '').trim().slice(0, 160),
  taxableLabel: submission.taxableLabel == null ? undefined : String(submission.taxableLabel || '').trim().slice(0, 220),
});
const TAX_ASSET_TYPES = new Set(['saldo', 'linear', 'none', 'gtk']);
const TAX_SALDO_GROUPS = [
  { id: 'a', code: 'A', label: 'Kontormaskiner mv.', rate: 30 },
  { id: 'b', code: 'B', label: 'Ervervet forretningsverdi', rate: 20 },
  { id: 'c', code: 'C', label: 'Vogntog, lastebiler, busser, varebiler mv.', rate: 24 },
  { id: 'd', code: 'D', label: 'Personbiler, maskiner og inventar mv.', rate: 20 },
  { id: 'e', code: 'E', label: 'Skip, fartøyer, rigger mv.', rate: 14 },
  { id: 'f', code: 'F', label: 'Fly, helikopter', rate: 12 },
  { id: 'g', code: 'G', label: 'Kraftanlegg og elektroteknisk utrustning', rate: 5 },
  { id: 'h', code: 'H', label: 'Bygg og anlegg, hoteller mv.', rate: 4 },
  { id: 'i', code: 'I', label: 'Forretningsbygg', rate: 2 },
  { id: 'j', code: 'J', label: 'Fast teknisk installasjon i bygninger', rate: 10 },
];
const normalizeTaxAssetValues = (values = {}) => {
  if (!values || typeof values !== 'object' || Array.isArray(values)) return {};
  return Object.fromEntries(Object.entries(values).map(([key, value]) => [
    String(key),
    value == null || value === '' ? null : (typeof value === 'number' ? Math.round(value * 100) / 100 : String(value)),
  ]));
};
const normalizeTaxAssetsModel = (taxAssets = {}) => ({
  items: safeArray(taxAssets.items)
    .map((entry) => stripUndefinedDeep({
      id: String(entry?.id || `tax-asset-${Date.now()}-${Math.random().toString(16).slice(2)}`),
      type: TAX_ASSET_TYPES.has(entry?.type) ? entry.type : 'saldo',
      objectId: Math.max(1, Number(entry?.objectId) || 1),
      source: entry?.source === 'fixed-assets' ? 'fixed-assets' : 'manual',
      values: normalizeTaxAssetValues(entry?.values),
    }))
    .filter((entry) => entry.id),
});

const taxAssetGroupCode = (value) => String(value || '').trim().toUpperCase();
const taxAssetGroupId = (value) => taxAssetGroupCode(value).toLowerCase();
const taxAssetGroupConfig = (groupId) =>
  TAX_SALDO_GROUPS.find((group) => group.id === taxAssetGroupId(groupId) || group.code === taxAssetGroupCode(groupId)) || TAX_SALDO_GROUPS[3];
const taxAssetRate = (groupId) => taxAssetGroupConfig(groupId).rate;
const taxAssetComputedValues = (type, values = {}) => {
  const n=(key)=>num(values[key]);
  const grunnlag=n('inngaende')+n('nyanskaffelse')+n('pakostning')-n('justeringMva')-n('vederlag');
  if(type==='gtk'){const base=n('inngaende')+n('sumGevinst')-n('sumTap'); const rate=values.sats==null?20:n('sats');const recognised=base*rate/100;return {grunnlag:base,inntekt:Math.max(0,recognised),inntektsfradrag:Math.max(0,-recognised),utgaende:base-recognised};}
  return {grunnlag,utgaende:Math.round((grunnlag-n('avskrivning')+n('inntektNegSaldo')+n('gevinstOverf')-n('tapOverf'))*100)/100};
};
const taxAssetSpecsFromFixedAssets = (state, year) => {
  const tax=window.PlummeFixedAssetRegister.build(state,year).tax;
  const items=tax.rows.map((row,index)=>({id:`fixed-assets-${year}-${row.id}`,type:['none','linear'].includes(row.g)?row.g:'saldo',objectId:index+1,source:'fixed-assets',values:{grp:row.g.toUpperCase(),beskrivelse:row.info.label,inngaende:row.inn,nyanskaffelse:row.ny,pakostning:row.improvements,justeringMva:row.reductions,vederlag:row.salg,sats:row.rate,avskrivning:row.dep,inntektNegSaldo:row.income,gevinstOverf:Math.max(0,row.transfer),tapOverf:Math.max(0,-row.transfer),grunnlag:row.base,utgaende:row.ut}}));
  const gtk=tax.gainLoss;
  if(gtk&&(gtk.opening||gtk.transfers||gtk.closing)) items.push({id:`fixed-assets-${year}-gtk`,type:'gtk',objectId:items.length+1,source:'fixed-assets',values:{beskrivelse:t('asset.gainLossAccount'),inngaende:gtk.opening,sumGevinst:Math.max(0,gtk.transfers),sumTap:Math.max(0,-gtk.transfers),grunnlag:gtk.base,inntekt:Math.max(0,gtk.income),inntektsfradrag:Math.max(0,-gtk.income),utgaende:gtk.closing}});
  return items;
};

const taxReturnAssetsModel = (state, year, processTaxAssets = {}) => {
  const normalized = normalizeTaxAssetsModel(processTaxAssets);
  const automatic = taxAssetSpecsFromFixedAssets(state, year);
  const manual = normalized.items.filter((entry) => entry.source !== 'fixed-assets');
  return {
    items: [...automatic, ...manual].map((entry) => ({
      ...entry,
      values: {
        ...entry.values,
        ...(entry.type === 'saldo' && entry.values.grp ? { sats: entry.values.sats ?? taxAssetRate(entry.values.grp) } : {}),
        ...(entry.source==='fixed-assets'?{}:taxAssetComputedValues(entry.type, entry.values)),
      },
    })),
    manualItems: manual,
  };
};

const TAX_OTHER_INVESTMENT_TYPES = new Set(['aksje_reg', 'aksje_ikke', 'verdipapirfond', 'obligasjon', 'virtuell', 'fondskonto', 'annet', 'daans']);
const normalizeTaxOtherYesNo = (value) => {
  if (value === undefined) return undefined;
  const clean = String(value || '').trim().toLowerCase();
  if (clean === 'ja' || clean === 'yes') return 'Ja';
  if (clean === 'nei' || clean === 'no') return 'Nei';
  return '';
};
const normalizeTaxOtherValues = (values = {}) => {
  if (!values || typeof values !== 'object' || Array.isArray(values)) return {};
  return Object.fromEntries(Object.entries(values).map(([key, value]) => [
    String(key),
    value == null || value === '' ? null : (typeof value === 'number' ? Math.round(value * 100) / 100 : String(value)),
  ]));
};
const normalizeTaxOtherModel = (taxOther = {}) => {
  const ownerBenefits = taxOther.ownerBenefits && typeof taxOther.ownerBenefits === 'object' ? taxOther.ownerBenefits : {};
  const companyInfo = taxOther.companyInfo && typeof taxOther.companyInfo === 'object' ? taxOther.companyInfo : {}; const coverage = window.PlummeTaxReturnReadiness.normalizeOtherCoverage(taxOther);
  return {
    ownerBenefits: stripUndefinedDeep({
      dividendPaid: ownerBenefits.dividendPaid == null || ownerBenefits.dividendPaid === '' ? null : Math.round(num(ownerBenefits.dividendPaid) * 100) / 100,
      companyToOwnerBenefit: normalizeTaxOtherYesNo(ownerBenefits.companyToOwnerBenefit),
      ownerToCompanyBenefit: normalizeTaxOtherYesNo(ownerBenefits.ownerToCompanyBenefit),
      notes: ownerBenefits.notes == null ? null : String(ownerBenefits.notes || '').trim().slice(0, 1000),
      ...coverage.ownerBenefits,
    }),
    companyInfo: stripUndefinedDeep({
      foreignOwnershipOrCrossBorderReorg: normalizeTaxOtherYesNo(companyInfo.foreignOwnershipOrCrossBorderReorg),
      wasGroupSubsidiary: normalizeTaxOtherYesNo(companyInfo.wasGroupSubsidiary),
      taxFreeTransferOfAssetsOrLiabilities: normalizeTaxOtherYesNo(companyInfo.taxFreeTransferOfAssetsOrLiabilities),
      ...coverage.companyInfo,
      notes: companyInfo.notes == null ? null : String(companyInfo.notes || '').trim().slice(0, 1000),
    }),
    soleTrader: coverage.soleTrader, investments: safeArray(taxOther.investments)
      .map((entry) => stripUndefinedDeep({
        id: String(entry?.id || `tax-other-${Date.now()}-${Math.random().toString(16).slice(2)}`),
        type: TAX_OTHER_INVESTMENT_TYPES.has(entry?.type) ? entry.type : 'annet',
        source: entry?.source === 'trial-balance' ? 'trial-balance' : 'manual',
        values: normalizeTaxOtherValues(entry?.values),
        openSections: safeArray(entry?.openSections).map(String).filter(Boolean),
      }))
      .filter((entry) => entry.id),
  };
};

const taxOtherInvestmentTypeFromRow = (row = {}) => {
  const label = String(row.label || '').toLowerCase();
  const code = String(row.code || '');
  if (/fond/.test(label)) return 'verdipapirfond';
  if (/obligasjon|sertifikat/.test(label)) return 'obligasjon';
  if (/krypto|bitcoin|ethereum|virtuell/.test(label)) return 'virtuell';
  if (/deltaker|ans|da\b/.test(label)) return 'daans';
  if (/aksje|andel/.test(label) || /^18/.test(code)) return 'aksje_reg';
  return 'annet';
};

const taxOtherInvestmentsFromWealth = (wealth = {}) =>
  safeArray(wealth.sharesInvestmentRows).map((row, index) => stripUndefinedDeep({
    id: `trial-investment-${row.code || index}`,
    type: taxOtherInvestmentTypeFromRow(row),
    source: 'trial-balance',
    values: {
      selskapnavn: row.label,
      navn: row.label,
      fondnavn: row.label,
      finansid: row.code,
      formue: Math.max(0, num(row.amount)),
    },
    openSections: [],
  }));

const taxReturnOtherModel = (state, processTaxOther = {}, wealth = {}) => {
  const normalized = normalizeTaxOtherModel(processTaxOther);
  const automatic = taxOtherInvestmentsFromWealth(wealth);
  const manual = normalized.investments.filter((entry) => entry.source !== 'trial-balance');
  const settings = state.settings || {};
  return {
    ownerBenefits: {
      dividendPaid: null,
      companyToOwnerBenefit: '',
      ownerToCompanyBenefit: '',
      ...(normalized.ownerBenefits || {}),
    },
    companyInfo: {
      foreignOwnershipOrCrossBorderReorg: settings.foreignOwnershipOrCrossBorderReorg ? 'Ja' : '',
      wasGroupSubsidiary: settings.groupSubsidiary ? 'Ja' : '',
      taxFreeTransferOfAssetsOrLiabilities: '',
      ownsProperty: '',
      ...(normalized.companyInfo || {}),
    },
    soleTrader: normalized.soleTrader || {},
    investments: [...automatic, ...manual],
    manualInvestments: manual,
  };
};

const normalizeTaxIncomeBalanceValue = (value, depth = 0) => {
  if (depth > 4) return null;
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Math.round(value * 100) / 100;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.trim().slice(0, 2000);
  if (Array.isArray(value)) return value.slice(0, 100).map((entry) => normalizeTaxIncomeBalanceValue(entry, depth + 1));
  if (typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).slice(0, 100).map(([key, entry]) => [
      String(key).slice(0, 120),
      normalizeTaxIncomeBalanceValue(entry, depth + 1),
    ]));
  }
  return String(value).trim().slice(0, 2000);
};

const normalizeTaxIncomeBalanceValues = (values = {}) => {
  if (!values || typeof values !== 'object' || Array.isArray(values)) return {};
  return Object.fromEntries(Object.entries(values).map(([key, value]) => [
    String(key).slice(0, 120),
    normalizeTaxIncomeBalanceValue(value),
  ]));
};

const normalizeTaxIncomeBalanceModel = (value = {}) => ({
  posts: safeArray(value.posts)
    .map((entry) => {
      const kind = ['income', 'cost', 'balance'].includes(entry?.kind) ? entry.kind : 'balance';
      return stripUndefinedDeep({
        id: String(entry?.id || `tax-income-balance-post-${Date.now()}-${Math.random().toString(16).slice(2)}`),
        kind,
        code: String(entry?.code || '').trim(),
        label: String(entry?.label || '').trim(),
        amount: entry?.amount == null || entry?.amount === '' ? null : Math.round(num(entry.amount) * 100) / 100,
        wealthValue: entry?.wealthValue == null || entry?.wealthValue === '' ? null : Math.round(num(entry.wealthValue) * 100) / 100,
        description: entry?.description == null ? null : String(entry.description || '').trim().slice(0, 500),
      });
    })
    .filter((entry) => entry.id && entry.code && entry.label),
  specs: safeArray(value.specs)
    .map((entry) => stripUndefinedDeep({
      id: String(entry?.id || `tax-income-balance-spec-${Date.now()}-${Math.random().toString(16).slice(2)}`),
      type: String(entry?.type || '').trim(),
      title: String(entry?.title || '').trim(),
      values: normalizeTaxIncomeBalanceValues(entry?.values),
    }))
    .filter((entry) => entry.id && entry.type && entry.title),
  equityChanges: safeArray(value.equityChanges)
    .map((entry) => stripUndefinedDeep({
      id: String(entry?.id || `tax-equity-change-${Date.now()}-${Math.random().toString(16).slice(2)}`),
      type: String(entry?.type || '').trim(),
      sign: Number(entry?.sign) === -1 ? -1 : 1,
      amount: entry?.amount == null || entry?.amount === '' ? null : Math.round(num(entry.amount) * 100) / 100,
      description: entry?.description == null ? null : String(entry.description || '').trim().slice(0, 500),
    }))
    .filter((entry) => entry.id && entry.type),
  principleChanges: safeArray(value.principleChanges)
    .map((entry) => stripUndefinedDeep({
      id: String(entry?.id || `tax-principle-change-${Date.now()}-${Math.random().toString(16).slice(2)}`),
      code: String(entry?.code || '').trim(),
      label: String(entry?.label || '').trim(),
      positive: entry?.positive == null || entry?.positive === '' ? null : Math.round(num(entry.positive) * 100) / 100,
      negative: entry?.negative == null || entry?.negative === '' ? null : Math.round(num(entry.negative) * 100) / 100,
      description: entry?.description == null ? null : String(entry.description || '').trim().slice(0, 500),
    }))
    .filter((entry) => entry.id && entry.code && entry.label),
});

// Speiler functions/accounting/norwegianTaxRates.ts. `null` betyr «ma avklares
// faglig» — aldri 0. Uten arsavgrensning fikk eldre ar dagens sats.
